import React, { useMemo } from "react";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import placeholder from "../../../assets/player_placeholder.png";
import PlayerName from "./playerName";
import { getPlayerPhoto } from "../functions";
import "./playerSlot.sass";

// ============================================================================
// FUNCIONES HELPER PARA ESTILOS
// ============================================================================

/**
 * Genera estilos para el avatar activo
 * @param {boolean} isActive - Si el slot está activo
 * @param {string} accentColor - Color de acento (hex o CSS var)
 * @returns {Object|undefined} Objeto de estilos o undefined
 */
function getActiveStyle(isActive, accentColor) {
  return isActive && accentColor
    ? { boxShadow: `0 0 0 3px ${accentColor}` }
    : undefined;
}

/**
 * Calcula los estados de validación de drag & drop
 * @param {Object|null} draggingPlayer - Jugador siendo arrastrado
 * @returns {Object} Objeto con estados isDragOver, isValidDrop, isInvalidDrop
 */
function calculateDragStates(draggingPlayer) {
  const isDragOver = draggingPlayer !== null;
  // Sin restricción de posición: cualquier jugador puede ir a cualquier slot
  const isValidDrop = isDragOver;
  const isInvalidDrop = false;

  return { isDragOver, isValidDrop, isInvalidDrop };
}

// ============================================================================
// COMPONENTE PLAYERSLOT
// ============================================================================

/**
 * Slot de jugador en la cancha con soporte para drag & drop
 * 
 * Renderiza un círculo posicionado en la cancha que puede:
 * - Mostrar un jugador asignado o un número de slot vacío
 * - Ser seleccionado (activo) con estilos visuales
 * - Recibir jugadores arrastrados (drag & drop)
 * - Validar compatibilidad de roles
 * - Mostrar estados visuales (válido/inválido)
 * - Eliminar jugadores asignados
 * 
 * @component
 * @param {Object} slot - Configuración del slot
 * @param {string} slot.id - Identificador único del slot (ej: "gk", "rb")
 * @param {string} slot.label - Etiqueta visible (ej: "POR", "LD")
 * @param {string} slot.role - Rol genérico (ej: "Portero", "Defensa")
 * @param {string} slot.roleKey - Rol específico para validación (ej: "POR", "LD")
 * @param {number} slot.x - Posición horizontal en porcentaje (0-100)
 * @param {number} slot.y - Posición vertical en porcentaje (0-100)
 * 
 * @param {number} slotNumber - Número del slot para mostrar cuando está vacío
 * 
 * @param {Object|null} player - Jugador asignado al slot
 * @param {string} player.firstName - Primer nombre del jugador
 * @param {string} player.lastName - Apellido del jugador
 * @param {string} player.photo - URL de la foto del jugador
 * @param {string[]} player.roles - Array de roles que puede jugar
 * 
 * @param {boolean} isActive - Si el slot está activo/seleccionado
 * @param {Function} onClick - Callback al hacer click en el slot
 * @param {Function} onClear - Callback para eliminar el jugador (recibe slot.id)
 * 
 * @param {string} [accentColor] - Color de acento para estilos (hex o CSS var)
 * @param {string} [nameColor] - Color del nombre del jugador
 * @param {string} [lastNameColor] - Color del apellido del jugador
 * @param {string} [teamImage] - Imagen del equipo como placeholder cuando no hay jugador asignado
 * @param {string} [backgroundTeam] - Color de fondo del círculo del avatar (fallback: var(--color-base-diamond))
 * 
 * @param {Object|null} draggingPlayer - Jugador siendo arrastrado (null si no hay)
 * @param {string[]} draggingPlayer.roles - Roles del jugador siendo arrastrado
 * 
 * @returns {JSX.Element} Elemento div con el slot del jugador
 */
export default React.memo(function PlayerSlot({ slot, slotNumber, player, isActive, onClick, onClear, accentColor, nameColor, lastNameColor, draggingPlayer, teamImage, backgroundTeam, selectionPath }) {
  const hasPlayer = !!player;

  // Memoizar validaciones de drag & drop
  const dragStates = useMemo(() => {
    return calculateDragStates(draggingPlayer);
  }, [draggingPlayer]);

  const { isDragOver, isValidDrop, isInvalidDrop } = dragStates;

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `slot-${slot.id}`,
    data: { slotId: slot.id, slotRole: slot.role, slotRoleKey: slot.roleKey },
  });

  // El slot es draggable solo cuando tiene un jugador asignado
  const { setNodeRef: setDragRef, listeners, attributes, isDragging } = useDraggable({
    id: `slot-drag-${slot.id}`,
    data: { playerId: player?.id, sourceSlotId: slot.id },
    disabled: !hasPlayer,
  });

  // Combina las refs de droppable y draggable en el mismo elemento
  const setNodeRef = (node) => {
    setDropRef(node);
    setDragRef(node);
  };

  // Memoizar construcción de classNames
  const classNames = useMemo(() => {
    return [
      "player-slot",
      isActive && "player-slot--active",
      hasPlayer && "player-slot--filled",
      isDragging && "player-slot--dragging",
      isOver && isValidDrop && "player-slot--drop-valid",
      isOver && isInvalidDrop && "player-slot--drop-invalid",
      !isOver && isValidDrop && "player-slot--drop-target",
      !isOver && isInvalidDrop && "player-slot--drop-blocked",
    ].filter(Boolean).join(" ");
  }, [isActive, hasPlayer, isDragging, isOver, isValidDrop, isInvalidDrop]);

  const handleRemove = (e) => {
    e.stopPropagation();
    onClear(slot.id);
  };

  return (
    <div
      ref={setNodeRef}
      className={classNames}
      style={{ left: `${slot.x}%`, top: `${slot.y}%` }}
      onClick={onClick}
      role="button"
      aria-label={hasPlayer ? `${slot.label}: ${player.firstName} ${player.lastName}` : `Select ${slot.label}`}
      {...(hasPlayer ? listeners : {})}
      {...(hasPlayer ? attributes : {})}
    >
      <div className="player-slot__avatar" style={{...getActiveStyle(isActive, accentColor), background: backgroundTeam || "var(--color-base-diamond)"}}>
        <img
          src={player?.photo ? getPlayerPhoto(player.photo, selectionPath) : (teamImage || placeholder)}
          alt={player ? `${player.firstName} ${player.lastName}` : slot.label}
          onError={(e) => { e.target.src = placeholder; }}
        />
      </div>

      {hasPlayer && (
        <button
          className="player-slot__remove"
          onClick={handleRemove}
          aria-label={`Remove ${player.firstName} ${player.lastName}`}
        >
          <span className="player-slot__remove-icon player-slot__remove-icon--check">✓</span>
          <span className="player-slot__remove-icon player-slot__remove-icon--close">✕</span>
        </button>
      )}

      {player &&
        <PlayerName
          player={player}
          nameColor={nameColor}
          lastNameColor={lastNameColor}
          slotNumber={slotNumber}
        />
      }
    </div>
  );
});